import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  TrendingUp,
  Users,
  MessageSquare,
  Share2,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Play,
  Pause,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Download,
  Target,
  Activity
} from 'lucide-react';
import { SocialMediaAnalytics, CrossPlatformMetrics, EngagementTrend } from '../../services/integrations/social/SocialMediaAnalytics';
import { CrossPlatformScheduler, ScheduledContent } from '../../services/integrations/social/CrossPlatformScheduler';
import CampaignTimelineView from './CampaignTimelineView';
import ConflictResolutionUI from './ConflictResolutionUI';
import { tone, toneForStatus } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

interface CampaignTimelineEvent {
  id: string;
  type: 'email' | 'social' | 'conflict';
  title: string;
  platform?: string;
  scheduledTime: Date;
  status: 'scheduled' | 'published' | 'failed' | 'conflict';
  engagement?: {
    likes: number;
    comments: number;
    shares: number;
    reach: number;
  };
}

interface CrossChannelCampaign {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'paused';
  channels: Array<{
    type: 'email' | 'social';
    platform?: string;
    enabled: boolean;
  }>;
  timeline: CampaignTimelineEvent[];
  performance: {
    totalReach: number;
    totalEngagement: number;
    conversionRate: number;
    roi: number;
  };
}

const CrossChannelCoordinationDashboard: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CrossChannelCampaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CrossChannelCampaign | null>(null);
  const [scheduledContent, setScheduledContent] = useState<ScheduledContent[]>([]);
  const [crossPlatformMetrics, setCrossPlatformMetrics] = useState<CrossPlatformMetrics | null>(null);
  const [_engagementTrends, setEngagementTrends] = useState<EngagementTrend[]>([]);
  const [activeView, setActiveView] = useState<'overview' | 'campaigns' | 'scheduler' | 'analytics' | 'conflicts'>('overview');
  const [loading, setLoading] = useState(true);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showScheduleContent, setShowScheduleContent] = useState(false);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [_mockConflicts, _setMockConflicts] = useState<any[]>([]);

  // Initialize services
  const [analytics] = useState(() => new SocialMediaAnalytics());
  const [scheduler] = useState(() => new CrossPlatformScheduler());

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      startDate.setDate(endDate.getDate() - days);

      // Load data in parallel
      const [metricsData, trendsData, scheduledData] = await Promise.all([
        analytics.generateCrossPlatformMetrics({ start: startDate, end: endDate }),
        analytics.getEngagementTrends({ start: startDate, end: endDate }),
        Promise.resolve(scheduler.listScheduledContent())
      ]);

      setCrossPlatformMetrics(metricsData);
      setEngagementTrends(trendsData);
      setScheduledContent(scheduledData);

      // Load mock campaigns
      loadMockCampaigns();

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMockCampaigns = () => {
    const mockCampaigns: CrossChannelCampaign[] = [
      {
        id: 'campaign_1',
        name: 'Summer Product Launch',
        description: 'Coordinated launch campaign across email and social media',
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        status: 'active',
        channels: [
          { type: 'email', enabled: true },
          { type: 'social', platform: 'linkedin', enabled: true },
          { type: 'social', platform: 'twitter', enabled: true },
          { type: 'social', platform: 'facebook', enabled: true }
        ],
        timeline: [
          {
            id: 'event_1',
            type: 'email',
            title: 'Launch Announcement Email',
            scheduledTime: new Date(Date.now() + 2 * 60 * 60 * 1000),
            status: 'scheduled'
          },
          {
            id: 'event_2',
            type: 'social',
            title: 'Product Teaser Post',
            platform: 'linkedin',
            scheduledTime: new Date(Date.now() + 3 * 60 * 60 * 1000),
            status: 'scheduled'
          }
        ],
        performance: {
          totalReach: 15420,
          totalEngagement: 892,
          conversionRate: 3.2,
          roi: 245
        }
      },
      {
        id: 'campaign_2',
        name: 'Holiday Promotion',
        description: 'Multi-channel holiday marketing campaign',
        startDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        endDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        channels: [
          { type: 'email', enabled: true },
          { type: 'social', platform: 'instagram', enabled: true },
          { type: 'social', platform: 'facebook', enabled: true }
        ],
        timeline: [],
        performance: {
          totalReach: 0,
          totalEngagement: 0,
          conversionRate: 0,
          roi: 0
        }
      }
    ];

    setCampaigns(mockCampaigns);
  };

  const getStatusColor = (status) => tone(toneForStatus(status)).chip;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play className="w-4 h-4" />;
      case 'scheduled': return <Clock className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'paused': return <Pause className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'conflict': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Active Campaigns</p>
              <p className="text-2xl font-bold text-content">
                {campaigns.filter(c => c.status === 'active').length}
              </p>
            </div>
            <Target className="w-8 h-8 text-blue-600 dark:text-blue-300" />
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Total Reach</p>
              <p className="text-2xl font-bold text-content">
                {crossPlatformMetrics ? formatNumber(crossPlatformMetrics.totalReach) : '0'}
              </p>
            </div>
            <Users className="w-8 h-8 text-green-600 dark:text-green-300" />
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Engagement Rate</p>
              <p className="text-2xl font-bold text-content">
                {crossPlatformMetrics ? crossPlatformMetrics.averageEngagementRate.toFixed(1) + '%' : '0%'}
              </p>
            </div>
            <Activity className="w-8 h-8 text-purple-600 dark:text-purple-300" />
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-secondary">Scheduled Posts</p>
              <p className="text-2xl font-bold text-content">
                {scheduledContent.filter(c => c.status === 'scheduled').length}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-orange-600 dark:text-orange-300" />
          </div>
        </div>
      </div>

      {/* Recent Campaigns */}
      <div className="bg-surface rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-content">Recent Campaigns</h3>
            <button
              onClick={() => setShowCreateCampaign(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {campaigns.slice(0, 3).map((campaign) => (
              <div key={campaign.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700">
                <div className="flex items-center gap-4">
                  <div>
                    <h4 className="font-medium text-content">{campaign.name}</h4>
                    <p className="text-sm text-content-secondary">{campaign.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatNumber(campaign.performance.totalReach)} reach</p>
                    <p className="text-sm text-content-secondary">{campaign.performance.conversionRate}% conversion</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(campaign.status)}`}>
                    {getStatusIcon(campaign.status)}
                    {campaign.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Platform Performance */}
      {crossPlatformMetrics && (
        <div className="bg-surface rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-content">Platform Performance</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(crossPlatformMetrics.platformBreakdown).map(([platform, metrics]) => (
                <div key={platform} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-content capitalize">{platform}</h4>
                    <span className="text-sm text-content-secondary">{metrics.posts} posts</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-content-secondary">Reach</span>
                      <span className="text-sm font-medium">{formatNumber(metrics.reach)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-content-secondary">Engagement</span>
                      <span className="text-sm font-medium">{formatNumber(metrics.engagement)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-content-secondary">Rate</span>
                      <span className="text-sm font-medium">{metrics.engagementRate.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderCampaigns = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-content">Cross-Channel Campaigns</h2>
        <button
          onClick={() => setShowCreateCampaign(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Campaign
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {campaigns.map((campaign) => (
          <div key={campaign.id} className="bg-surface rounded-lg shadow-sm border">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-content">{campaign.name}</h3>
                  <p className="text-content-secondary mt-1">{campaign.description}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(campaign.status)}`}>
                  {getStatusIcon(campaign.status)}
                  {campaign.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-content-secondary">Start Date</p>
                  <p className="font-medium">{campaign.startDate.toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-content-secondary">End Date</p>
                  <p className="font-medium">{campaign.endDate.toLocaleDateString()}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-content-secondary mb-2">Channels</p>
                <div className="flex flex-wrap gap-2">
                  {campaign.channels.map((channel, index) => (
                    <span
                      key={index}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        channel.enabled ? 'bg-green-100 text-green-600 dark:text-green-300' : 'bg-gray-100 dark:bg-dark-700 text-content-secondary'
                      }`}
                    >
                      {channel.type === 'email' ? 'Email' : channel.platform}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-content-secondary">Total Reach</p>
                  <p className="text-lg font-semibold">{formatNumber(campaign.performance.totalReach)}</p>
                </div>
                <div>
                  <p className="text-sm text-content-secondary">Engagement</p>
                  <p className="text-lg font-semibold">{formatNumber(campaign.performance.totalEngagement)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedCampaign(campaign)}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                >
                  <Eye className="w-3 h-3" />
                  View Details
                </button>
                <button className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-50 dark:bg-dark-700 text-content-secondary rounded hover:bg-gray-100 dark:hover:bg-dark-700">
                  <Edit className="w-3 h-3" />
                  Edit
                </button>
                <button className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-50 dark:bg-dark-700 text-content-secondary rounded hover:bg-gray-100 dark:hover:bg-dark-700">
                  <BarChart3 className="w-3 h-3" />
                  Analytics
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderScheduler = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-content">Content Scheduler</h2>
        <button
          onClick={() => setShowScheduleContent(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Schedule Content
        </button>
      </div>

      <div className="bg-surface rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-content">Scheduled Content</h3>
            <div className="flex items-center gap-2">
              <SelectField className="border rounded px-3 py-1 text-sm">
                <option>All Platforms</option>
                <option>LinkedIn</option>
                <option>Twitter</option>
                <option>Facebook</option>
                <option>Instagram</option>
              </SelectField>
              <button aria-label="Refresh" className="p-1 text-content-secondary hover:text-content">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {scheduledContent.map((content) => (
              <div key={content.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-content">
                        {content.campaignId ? `Campaign Content` : 'Standalone Post'}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(content.status)}`}>
                        {getStatusIcon(content.status)}
                        {content.status}
                      </span>
                    </div>
                    <p className="text-sm text-content-secondary">
                      Platforms: {content.platforms.join(', ')}
                    </p>
                    <p className="text-sm text-content-secondary">
                      Scheduled: {content.scheduledTime.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button aria-label="Edit" className="p-1 text-content-secondary hover:text-content">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button aria-label="Delete" className="p-1 text-content-secondary hover:text-content">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {scheduledContent.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-content-subtle mx-auto mb-4" />
                <h3 className="text-lg font-medium text-content mb-2">No Scheduled Content</h3>
                <p className="text-content-secondary mb-4">Schedule your first cross-platform post to get started.</p>
                <button
                  onClick={() => setShowScheduleContent(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Schedule Content
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-content">Cross-Channel Analytics</h2>
        <div className="flex items-center gap-2">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-content">Email Performance</h3>
            <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-300" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Open Rate</span>
              <span className="text-sm font-medium">24.5%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Click Rate</span>
              <span className="text-sm font-medium">3.2%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Conversion Rate</span>
              <span className="text-sm font-medium">1.8%</span>
            </div>
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-content">Social Performance</h3>
            <Share2 className="w-6 h-6 text-green-600 dark:text-green-300" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Engagement Rate</span>
              <span className="text-sm font-medium">
                {crossPlatformMetrics ? crossPlatformMetrics.averageEngagementRate.toFixed(1) + '%' : '0%'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Total Reach</span>
              <span className="text-sm font-medium">
                {crossPlatformMetrics ? formatNumber(crossPlatformMetrics.totalReach) : '0'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Total Engagement</span>
              <span className="text-sm font-medium">
                {crossPlatformMetrics ? formatNumber(crossPlatformMetrics.totalEngagement) : '0'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-surface p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-content">Cross-Channel Impact</h3>
            <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-300" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Attribution Rate</span>
              <span className="text-sm font-medium">15.3%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Cross-Channel ROI</span>
              <span className="text-sm font-medium">285%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-content-secondary">Synergy Score</span>
              <span className="text-sm font-medium">8.2/10</span>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Trends Chart */}
      <div className="bg-surface rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-content">Engagement Trends</h3>
        </div>
        <div className="p-6">
          <div className="h-64 flex items-center justify-center text-content-muted">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-content-subtle mx-auto mb-4" />
              <p>Engagement trends chart would be displayed here</p>
              <p className="text-sm">Integration with charting library needed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Breakdown */}
      {crossPlatformMetrics && (
        <div className="bg-surface rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-content">Platform Breakdown</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(crossPlatformMetrics.platformBreakdown).map(([platform, metrics]) => (
                <div key={platform} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-content capitalize">{platform}</h4>
                    <span className="text-sm text-content-secondary">{metrics.posts} posts</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-content-secondary">Reach</span>
                      <span className="text-sm font-medium">{formatNumber(metrics.reach)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-content-secondary">Engagement</span>
                      <span className="text-sm font-medium">{formatNumber(metrics.engagement)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-content-secondary">Rate</span>
                      <span className="text-sm font-medium">{metrics.engagementRate.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2 mt-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${Math.min(metrics.engagementRate * 10, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderConflicts = () => {
    // Load mock conflicts for demonstration
    const mockConflictsData: Array<{
      id: string;
      type: 'timing' | 'audience_overlap' | 'content_similarity' | 'platform_limit';
      severity: 'low' | 'medium' | 'high' | 'critical';
      title: string;
      description: string;
      items: Array<{
        id: string;
        type: 'social_post' | 'email_campaign' | 'ad_campaign';
        title: string;
        platform?: string;
        scheduledTime: Date;
        duration?: number;
        audience?: {
          size: number;
          overlap?: number;
        };
        content?: {
          preview: string;
          hashtags?: string[];
          mentions?: string[];
        };
        performance?: {
          expectedReach: number;
          expectedEngagement: number;
        };
      }>;
      suggestions: Array<{
        id: string;
        type: 'reschedule' | 'modify_content' | 'change_audience' | 'merge_campaigns';
        title: string;
        description: string;
        effort: 'low' | 'medium' | 'high';
        impact: 'low' | 'medium' | 'high';
        estimatedImprovement: {
          reach?: number;
          engagement?: number;
          efficiency?: number;
        };
        actions: Array<{
          itemId: string;
          action: 'reschedule' | 'modify' | 'cancel' | 'merge';
          details: any;
        }>;
      }>;
      impact: {
        reachReduction?: number;
        engagementReduction?: number;
        audienceFatigue?: number;
      };
      detectedAt: Date;
      status: 'unresolved' | 'resolving' | 'resolved' | 'ignored';
      resolvedAt?: Date;
      resolvedBy?: string;
      resolution?: {
        suggestionId: string;
        appliedActions: Array<{
          itemId: string;
          action: string;
          originalValue: any;
          newValue: any;
          appliedAt: Date;
        }>;
        notes?: string;
      };
    }> = [
      {
        id: 'conflict_1',
        type: 'timing',
        severity: 'medium',
        title: 'Email and Social Post Timing Conflict',
        description: 'Email campaign and LinkedIn post scheduled within 30 minutes of each other',
        items: [
          {
            id: 'email_1',
            type: 'email_campaign',
            title: 'Product Launch Email',
            scheduledTime: new Date(Date.now() + 2 * 60 * 60 * 1000),
            audience: { size: 5000, overlap: 85 }
          },
          {
            id: 'social_1',
            type: 'social_post',
            title: 'Product Announcement Post',
            platform: 'linkedin',
            scheduledTime: new Date(Date.now() + 2.5 * 60 * 60 * 1000),
            audience: { size: 3200, overlap: 85 }
          }
        ],
        suggestions: [
          {
            id: 'suggestion_1',
            type: 'reschedule',
            title: 'Delay Social Post',
            description: 'Move LinkedIn post 2 hours later to avoid audience overlap',
            effort: 'low',
            impact: 'medium',
            estimatedImprovement: { reach: 15, engagement: 8 },
            actions: [
              {
                itemId: 'social_1',
                action: 'reschedule',
                details: { newTime: new Date(Date.now() + 4.5 * 60 * 60 * 1000) }
              }
            ]
          }
        ],
        impact: { reachReduction: 20, engagementReduction: 12, audienceFatigue: 15 },
        detectedAt: new Date(Date.now() - 30 * 60 * 1000),
        status: 'unresolved'
      }
    ];

    return (
      <ConflictResolutionUI
        conflicts={mockConflictsData}
        onResolveConflict={async (conflictId, resolution) => {
          console.log('Resolving conflict:', conflictId, resolution);
          // In real implementation, this would call the API
        }}
        onIgnoreConflict={async (conflictId, reason) => {
          console.log('Ignoring conflict:', conflictId, reason);
          // In real implementation, this would call the API
        }}
        onRefreshConflicts={async () => {
          console.log('Refreshing conflicts...');
          // In real implementation, this would reload conflicts from API
        }}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-300" />
        <span className="ml-2 text-content-secondary">Loading dashboard...</span>
      </div>
    );
  }

  const renderScheduleContentModal = () => {
    if (!showScheduleContent) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-content">Schedule Content</h2>
              <button
                onClick={() => setShowScheduleContent(false)}
                className="text-content-subtle hover:text-content-secondary"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Content Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Content Type
              </label>
              <SelectField className="w-full border rounded-lg px-3 py-2">
                <option>Social Media Post</option>
                <option>Email Campaign</option>
                <option>Cross-Channel Campaign</option>
              </SelectField>
            </div>

            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Platforms
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['LinkedIn', 'Twitter', 'Facebook', 'Instagram'].map((platform) => (
                  <label key={platform} className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{platform}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Content
              </label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 h-32"
                placeholder="Enter your content here..."
              />
            </div>

            {/* Scheduling */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            {/* Campaign Association */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Associate with Campaign (Optional)
              </label>
              <SelectField className="w-full border rounded-lg px-3 py-2">
                <option value="">Select a campaign...</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </SelectField>
            </div>

            {/* Conflict Detection */}
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-300" />
                <span className="font-medium text-yellow-800 dark:text-yellow-300">Potential Conflicts Detected</span>
              </div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                This post may conflict with an email campaign scheduled 30 minutes earlier. 
                Consider adjusting the timing for better audience engagement.
              </p>
            </div>
          </div>

          <div className="p-6 border-t bg-gray-50 dark:bg-dark-700 flex items-center justify-end gap-3">
            <button
              onClick={() => setShowScheduleContent(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-surface border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Here you would implement the actual scheduling logic
                console.log('Scheduling content...');
                setShowScheduleContent(false);
                // Show success message or update the scheduled content list
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Schedule Content
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateCampaignModal = () => {
    if (!showCreateCampaign) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-content">Create Campaign</h2>
              <button
                onClick={() => setShowCreateCampaign(false)}
                className="text-content-subtle hover:text-content-secondary"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Campaign Name
              </label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Enter campaign name..."
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 h-24"
                placeholder="Describe your campaign..."
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {/* Channel Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channels
              </label>
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" defaultChecked />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Email Marketing</span>
                </label>
                {['LinkedIn', 'Twitter', 'Facebook', 'Instagram'].map((platform) => (
                  <label key={platform} className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{platform}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Campaign Goals */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Goal
              </label>
              <SelectField className="w-full border rounded-lg px-3 py-2">
                <option>Brand Awareness</option>
                <option>Lead Generation</option>
                <option>Product Launch</option>
                <option>Customer Retention</option>
                <option>Event Promotion</option>
              </SelectField>
            </div>
          </div>

          <div className="p-6 border-t bg-gray-50 dark:bg-dark-700 flex items-center justify-end gap-3">
            <button
              onClick={() => setShowCreateCampaign(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-surface border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Here you would implement the actual campaign creation logic
                console.log('Creating campaign...');
                setShowCreateCampaign(false);
                // Show success message or update the campaigns list
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Campaign
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-content">Cross-Channel Coordination</h1>
          <p className="text-content-secondary mt-1">
            Manage and coordinate campaigns across email and social media platforms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SelectField
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
            className="border rounded px-3 py-2"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </SelectField>
          <button aria-label="Refresh"
            onClick={loadDashboardData}
            className="p-2 text-content-secondary hover:text-content border rounded"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex space-x-1 mb-8 bg-gray-100 dark:bg-dark-700 p-1 rounded-lg">
        {[
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'campaigns', label: 'Campaigns', icon: Target },
          { key: 'scheduler', label: 'Scheduler', icon: Calendar },
          { key: 'analytics', label: 'Analytics', icon: TrendingUp },
          { key: 'conflicts', label: 'Conflicts', icon: AlertTriangle }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveView(key as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeView === key
                ? ' text-blue-600 dark:text-blue-300 shadow-sm'
                : 'text-content-secondary hover:text-content'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeView === 'overview' && renderOverview()}
      {activeView === 'campaigns' && (
        selectedCampaign ? (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setSelectedCampaign(null)}
                className="text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
              >
                ← Back to Campaigns
              </button>
              <h2 className="text-2xl font-bold text-content">{selectedCampaign.name}</h2>
            </div>
            <CampaignTimelineView
              campaignId={selectedCampaign.id}
              onEventClick={(event) => console.log('Event clicked:', event)}
              onEventEdit={(event) => console.log('Edit event:', event)}
              onEventDelete={(eventId) => console.log('Delete event:', eventId)}
              onConflictResolve={(eventId, conflictId) => console.log('Resolve conflict:', eventId, conflictId)}
            />
          </div>
        ) : (
          renderCampaigns()
        )
      )}
      {activeView === 'scheduler' && renderScheduler()}
      {activeView === 'analytics' && renderAnalytics()}
      {activeView === 'conflicts' && renderConflicts()}

      {/* Schedule Content Modal */}
      {renderScheduleContentModal()}
      
      {/* Create Campaign Modal */}
      {renderCreateCampaignModal()}
    </div>
  );
};

export default CrossChannelCoordinationDashboard;